<template>
  <div class="mt-2.5">
    <!-- Style Selection Section -->
    <div class="mb-6">
      <div class="mb-1.5">
        <UITextSmMedium class="text-gray-700">{{ t("common.customstyle") }}</UITextSmMedium>
      </div>
      <div class="flex gap-2.5">
        <!-- Standard Style Option -->
        <div class="flex flex-col items-center gap-1.5">
          <div
            class="style-option"
            :class="{
              'style-option-active':
                !selectedElement.style || selectedElement.style === OPTION_FIELD_STYLES.STANDARD,
            }"
            @click="updateStyle(OPTION_FIELD_STYLES.STANDARD)"
          >
            <img
              :src="StandardRadioIcon"
              :alt="t('common.radioStyles.standardAlt')"
              class="h-[16px] w-[86px] object-contain"
            />
          </div>
          <UITextSmMedium class="text-gray-600">{{ t("common.standard") }}</UITextSmMedium>
        </div>

        <!-- Bubble Style Option -->
        <div class="flex flex-col items-center gap-1.5">
          <div
            class="style-option"
            :class="{ 'style-option-active': selectedElement.style === OPTION_FIELD_STYLES.BUBBLE }"
            @click="updateStyle(OPTION_FIELD_STYLES.BUBBLE)"
          >
            <img
              :src="BubbleRadioIcon"
              :alt="t('common.radioStyles.bubbleAlt')"
              class="h-[72px] w-[86px] object-contain"
            />
          </div>
          <UITextSmMedium class="text-gray-600">{{ t("common.bubble") }}</UITextSmMedium>
        </div>
      </div>
    </div>

    <div class="mb-2.5 flex items-center text-sm font-medium text-gray-700">
      <div class="ml-6 flex flex-1">
        <div v-if="!isQuiz" class="w-[47%]">{{ t("common.optionLabel") }}</div>
        <div v-if="enableCalculations">{{ t("common.calculationValue") }}</div>
      </div>
    </div>
    <VueDraggableNext
      v-model="regularOptions"
      handle=".handle"
      :class="[store.quizId ? 'space-y-8' : 'space-y-2']"
      @change="(e) => changeInOptions(e)"
    >
      <div
        v-for="(option, index) in regularOptions"
        :key="index"
        class="flex items-start justify-start space-x-2 self-stretch text-sm"
      >
        <Menu05Icon class="handle mt-2.5 h-4 w-4 cursor-move align-top text-gray-500" />
        <div :class="productBuilderClass">
          <UIInput
            :id="`option-label-${index}`"
            v-model="regularOptions[index].label"
            class="w-full"
            :placeholder="t('common.listEditor.labelPlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:options', [...regularOptions])"
          />
          <UIInputNumber
            v-if="enableCalculations"
            :id="`option-value-${index}`"
            v-model="regularOptions[index].calculatedValue"
            :show-button="false"
            size="large"
            class="w-full"
            :placeholder="t('common.listEditor.valuePlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:options', [...regularOptions])"
          />
          <div v-if="isQuiz" class="flex flex-col gap-3">
            <CategoryAndScore
              :index="index"
              :selected-category="getSelectedCategoryAndScore(index)"
              @update:value="categoryAndScoreChange"
            ></CategoryAndScore>
            <Switch
              :value="jumpEnabled[index]"
              :label="t('common.jumpTo')"
              @update:value="(value) => updateJumpTo(value, index)"
            />
            <div v-if="jumpEnabled[index]" class="space-y-1">
              <UITextSmMedium>{{ t("common.question") }}</UITextSmMedium>
              <UISelect
                :id="'radio' + index"
                :value="getSelectLogicQuestion(index)"
                :options="questionOptions"
                @update:value="
                  (value) => logicOptionSelection(value, option, index, selectedElement.uuid)
                "
                @change="store.addToHistory()"
              ></UISelect>
            </div>
          </div>
        </div>

        <UIButton
          id="remove-option"
          :disabled="isDeleteDisabled(index)"
          :aria-label="t('common.deleteOption')"
          @click="() => prepareToRemoveOption(index)"
        >
          <Trash01Icon class="h-5 w-5" />
        </UIButton>
      </div>
    </VueDraggableNext>
    <!-- Other Option Section -->
    <div
      v-if="!isQuiz && selectedElement?.allowCustomOption"
      class="mt-2 flex items-start justify-start space-x-2 self-stretch text-sm"
    >
      <Menu05Icon
        class="drag-handle-other mt-2.5 h-4 w-4 flex-shrink-0 align-top text-gray-500"
        style="opacity: 0.25"
      />
      <div class="flex flex-1 flex-col space-y-2">
        <div class="relative w-full">
          <UIInput
            :id="`other-option-label`"
            :model-value="t('common.otherOptionLabel')"
            class="w-full"
            :placeholder="t('common.otherOptionLabel')"
            readonly
          />
          <span
            class="other-chip pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-indigo-100 px-2.5 py-1 text-xs leading-4 text-blue-700"
          >
            {{ t("common.otherOptionLabel") }}
          </span>
        </div>
        <UIInput
          :id="`other-option-placeholder`"
          :model-value="otherOptionPlaceholderValue"
          class="w-full"
          :placeholder="otherOptionPlaceholderHint"
          @update:model-value="updateOtherOptionPlaceholder"
          @blur="commitOtherPlaceholder"
        />
      </div>
      <UIButton id="remove-other-option" @click="disableOtherOption">
        <Trash01Icon class="h-5 w-5" />
      </UIButton>
    </div>
    <div class="mt-4 flex flex-wrap items-center gap-4">
      <UIButton id="add-option" text :disabled="isLastOptionEmpty" @click="addOption">
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
      <UIButton
        v-if="!isQuiz && !selectedElement?.allowCustomOption"
        id="add-other-option"
        text
        class="!text-gray-600 hover:!text-gray-700"
        @click="enableOtherOption"
      >
        <PlusIcon class="mr-1.5 h-5 w-5 !text-gray-600" />
        {{ t("common.addOtherOption") }}
      </UIButton>
    </div>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import BubbleRadioIcon from "@/assets/img/bubble-radio.png";
import StandardRadioIcon from "@/assets/img/standard-radio.png";
import { useEmitter } from "@/composition/useEmitter";
import { useOtherOptionPlaceholderEditor } from "@/composition/useOtherOption";
import { useAppStore } from "@/store/app";
import { OPTION_FIELD_STYLES, type OptionFieldStyle } from "@/util/constants";
import { updateOptionsWithFocusPreservation } from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIInput, UIInputNumber, UISelect, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { isEqual } from "lodash";
import { computed, nextTick, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import Switch from "./../common/Elements/Switch.vue";
import CategoryAndScore from "./../quizzes/Elements/CategoryAndScore.vue";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";
const { t } = useI18n();

const store = useAppStore();
const emitter = useEmitter();

interface QuestionOption {
  value: string;
  label: string;
  [key: string]: any;
}

const props = defineProps({
  isQuiz: {
    type: [Boolean, String],
    default: false,
  },
  options: {
    type: [Array<string>, Array<any>],
    default: () => {
      return [];
    },
  },
  questionOptions: {
    type: Array as () => any[],
    default: () => {
      return [];
    },
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
  selectedElement: {
    type: Object,
    default: () => {
      return {};
    },
  },
  slideIndex: {
    type: Number,
    default: 0,
  },
  elementIndex: {
    type: Number,
    default: 0,
  },
  enableCalculations: {
    type: Boolean,
    default: false,
  },
  hidePrefillValue: {
    type: Boolean,
    default: false,
  },
});

const jumpEnabled = ref({});
const activeInputId = ref<string | null>(null);
const activeInputPosition = ref<number | null>(null);
const isSyncingFromProps = ref(false);
const skipNextOptionsWatcher = ref(false);

const emit = defineEmits([
  "update:options",
  "logic:update",
  "update:style",
  "update:allowCustomOption",
]);

const initializeOptions = () => {
  let optionsArray = props.options || [{ label: "", calculatedValue: "" }];

  if (Array.isArray(props.options)) {
    if (typeof props.options[0] === "string") {
      optionsArray = props.options.map((option) => ({ label: option, calculatedValue: "" }));
    } else if (typeof props.options[0] === "object" && props.options[0] !== null) {
      optionsArray = props.options.map((option) => ({
        label: option.label || "",
        calculatedValue: option.calculatedValue !== undefined ? option.calculatedValue : "",
      }));
    }
  }

  return optionsArray;
};

const indexToRemove = ref(null);
const showDeleteModal = ref(false);
const errors = ref<string[]>([]);
const {
  otherPlaceholderValue: otherOptionPlaceholderValue,
  otherPlaceholderHint: otherOptionPlaceholderHint,
  updateOtherPlaceholder: updateOtherOptionPlaceholder,
  commitOtherPlaceholder,
} = useOtherOptionPlaceholderEditor({
  element: computed(() => props.selectedElement),
  store,
  t,
});
const enableOtherOption = () => emit("update:allowCustomOption", true);

const disableOtherOption = () => {
  emit("update:allowCustomOption", false);
};

// Regular options (excluding "Other" option which is handled separately)
const regularOptions = ref(initializeOptions());

const onInputFocus = (e: FocusEvent) => {
  const target = e.target as HTMLInputElement;
  if (target?.id) {
    activeInputId.value = target.id;
  }
};

const validateOptions = () => {
  errors.value = regularOptions.value?.map((option) =>
    option?.label.trim() === "" ? t("common.listEditor.optionRequiredError") : ""
  );
};

watch(
  () => props.options,
  (newOptions) => {
    if (!isEqual(newOptions, regularOptions.value)) {
      isSyncingFromProps.value = true;
      regularOptions.value = updateOptionsWithFocusPreservation(
        newOptions,
        regularOptions.value,
        { label: "", calculatedValue: "" },
        activeInputId,
        activeInputPosition
      );
      nextTick(() => {
        isSyncingFromProps.value = false;
      });
    }
  },
  { deep: true }
);

watch(
  () => regularOptions.value,
  () => {
    validateOptions();
    if (isSyncingFromProps.value) {
      return;
    }
    if (skipNextOptionsWatcher.value) {
      skipNextOptionsWatcher.value = false;
      return;
    }
    emit("update:options", [...regularOptions.value]);
  },
  { deep: true }
);

watch(
  () => props.enableCalculations,
  (newValue) => {
    if (regularOptions.value && regularOptions.value.length) {
      regularOptions.value = regularOptions.value.map((option) => {
        if (typeof option === "string") {
          return { label: option, calculatedValue: "" };
        }
        return {
          label: option.label || "",
          calculatedValue: option.calculatedValue !== undefined ? option.calculatedValue : "",
        };
      });
      emit("update:options", [...regularOptions.value]);
    }
  }
);

const productBuilderClass = computed(() => {
  if (store.quizId) {
    return "flex w-[275px] flex-1 flex-col gap-3 self-stretch";
  }
  return "flex flex-1 space-x-2";
});

const isLastOptionEmpty = computed(() => {
  const lastOption = regularOptions.value[regularOptions.value.length - 1];
  return lastOption?.label.trim() === "";
});

const addOption = () => {
  skipNextOptionsWatcher.value = true;
  regularOptions.value.push({ label: "", calculatedValue: "" });
  emit("update:options", [...regularOptions.value]);
  store.addToHistory();
};

const isDeleteDisabled = (index: number): boolean => {
  if (props.hidePrefillValue) {
    return true;
  }
  if (!regularOptions.value[index]) {
    return true;
  }
  return false;
};

const updateStyle = (style: OptionFieldStyle) => {
  emit("update:style", style);
};

const prepareToRemoveOption = (index) => {
  // Prevent removing if only one regular option exists
  if (regularOptions.value.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      regularOptions.value.splice(index, 1);
      emit("update:options", [...regularOptions.value]);
      store.addToHistory();
    }
  }
};

const removeOption = async () => {
  if (indexToRemove.value !== null) {
    regularOptions.value.splice(indexToRemove.value, 1);
    emit("update:options", [...regularOptions.value]);
    store.addToHistory();
    emitter.emit("option:deleted", {
      indexToRemove: indexToRemove.value,
      current: props.selectedElement.uuid,
      slideIndex: props.slideIndex,
      elementIndex: props.elementIndex,
    });
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const getSelectLogicQuestion = (optionIndex) => {
  if (store.logic[props.selectedElement.uuid]) {
    const jumpToQuestion = store.logic[props.selectedElement.uuid][optionIndex];
    return jumpToQuestion?.jumpTo || "";
  }
  return "";
};

const setJumpToSwitch = () => {
  regularOptions.value.forEach((value, index) => {
    if (getSelectLogicQuestion(index)) {
      jumpEnabled.value[index] = true;
    }
  });
};

setJumpToSwitch();

const logicOptionSelection = (value, option, index, current) => {
  emitter.emit("logic:update", { jumpTo: value, option, index, current });
};

const categoryAndScoreChange = (category) => {
  emitter.emit("category:update", {
    ...category,
    elementIndex: props.elementIndex,
    slideIndex: props.slideIndex,
  });
};

const updateJumpTo = (value, index) => {
  jumpEnabled.value[index] = value;
};

const getSelectedCategoryAndScore = (index) => {
  if (props.selectedElement?.scoreByCategory) {
    return props.selectedElement.scoreByCategory[index] || {};
  }
  return {};
};

const changeInOptions = (e) => {
  emit("update:options", [...regularOptions.value]);
  emitter.emit("category:sort", {
    slideIndex: props.slideIndex,
    elementIndex: props.elementIndex,
    newIndex: e.moved.newIndex,
    oldIndex: e.moved.oldIndex,
  });
  emitter.emit("logic:sort", {
    newIndex: e.moved.newIndex,
    oldIndex: e.moved.oldIndex,
    current: props.selectedElement.uuid,
  });
};
</script>

<style scoped>
.style-option {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  padding: 12px 36px;
  background: #ffffff;
  transition: all 0.15s ease-in-out;
  height: 96px;
}

.style-option-active {
  border: 2px solid #155eef;
  background: #eff4ff;
}

.other-chip {
  border: 1px solid #c7d2fe;
}
</style>
